import { supabase } from '../lib/supabase'
import {
  buildPaymentRowsFromTemplates,
  mapPaymentTemplate,
  paymentTemplatesService,
  snapshotPaymentRows,
  snapshotTemplateRows,
} from './paymentTemplates'
import type {
  MarketingCampaign,
  MarketingCampaignInput,
  MarketingEvent,
  MarketingEventInput,
  MarketingInvoice,
  MarketingInvoiceAllocationInput,
  MarketingInvoiceFilters,
  MarketingInvoiceInput,
  MarketingInvoiceItemInput,
  MarketingPayment,
  MarketingPaymentFilters,
  MarketingPaymentGroupInput,
  MarketingPaymentListRow,
  SoftDeleteParams,
} from '../types/marketingNotas'
import { parseRequestNumberFilter } from '../utils/marketingRequestNumber'

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || ''
  return trimmed || null
}

async function syncSupplierQuoteStatus(
  quoteId: string | null | undefined,
  status: 'recebida' | 'nota_gerada'
): Promise<void> {
  if (!quoteId) return
  const { error } = await supabase
    .from('supplier_quotes')
    .update({ status })
    .eq('id', quoteId)
    .is('deleted_at', null)
  if (error) throw error
}

async function syncSupplierQuoteStatusSafe(
  quoteId: string | null | undefined,
  status: 'recebida' | 'nota_gerada',
  context: string
): Promise<void> {
  if (!quoteId) return
  try {
    await syncSupplierQuoteStatus(quoteId, status)
  } catch (error) {
    console.error(`Falha ao sincronizar status da cotação (${context}).`, error)
  }
}

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000
}

function normalizeNonNegativeMoney(
  value: number | null | undefined,
  fieldLabel: string
): number {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`Informe um valor de ${fieldLabel} válido.`)
  }
  return roundMoney(n)
}

function lineGross(item: MarketingInvoiceItemInput): number {
  return roundMoney(Number(item.quantity) * Number(item.unit_value))
}

function lineTotal(item: MarketingInvoiceItemInput): number {
  const discount = normalizeNonNegativeMoney(item.discount_value, 'desconto do item')
  const gross = lineGross(item)
  if (discount > gross + 0.00005) {
    throw new Error('O desconto do item não pode ser maior que o total da linha.')
  }
  if (item.total_value != null && Number.isFinite(item.total_value)) {
    return roundMoney(Math.max(0, Number(item.total_value)))
  }
  return roundMoney(Math.max(0, gross - discount))
}

function validateItems(
  documentType: MarketingInvoiceInput['document_type'],
  items: MarketingInvoiceItemInput[]
): void {
  if (!items.length) {
    throw new Error('Inclua ao menos um item na nota.')
  }
  for (const [index, item] of items.entries()) {
    const n = index + 1
    if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
      throw new Error(`Informe uma quantidade válida no item ${n}.`)
    }
    if (!Number.isFinite(item.unit_value) || item.unit_value < 0) {
      throw new Error(`Informe um valor unitário válido no item ${n}.`)
    }
    const discount = Number(item.discount_value ?? 0)
    if (!Number.isFinite(discount) || discount < 0) {
      throw new Error(`Informe um desconto válido no item ${n}.`)
    }
    if (discount > lineGross(item) + 0.00005) {
      throw new Error(`O desconto do item ${n} não pode ser maior que o total da linha.`)
    }
    if (documentType === 'nfe') {
      if (!item.stock_item_id?.trim()) {
        throw new Error(`Selecione o item de estoque na linha ${n}.`)
      }
    } else if (!item.description?.trim()) {
      throw new Error(`Informe a descrição do serviço na linha ${n}.`)
    }
  }
}

function normalizeFreightValue(
  documentType: MarketingInvoiceInput['document_type'],
  freightValue: number | null | undefined
): number {
  if (documentType !== 'nfe') return 0
  return normalizeNonNegativeMoney(freightValue, 'frete')
}

function normalizeDiscountValue(discountValue: number | null | undefined): number {
  return normalizeNonNegativeMoney(discountValue, 'desconto')
}

function invoiceItemsTotal(items: MarketingInvoiceItemInput[]): number {
  return roundMoney(items.reduce((acc, item) => acc + lineTotal(item), 0))
}

function invoiceExpectedTotal(
  documentType: MarketingInvoiceInput['document_type'],
  items: MarketingInvoiceItemInput[],
  freightValue: number,
  discountValue: number
): number {
  const freight = documentType === 'nfe' ? freightValue : 0
  return roundMoney(invoiceItemsTotal(items) + freight - discountValue)
}

function validatePayments(
  documentType: MarketingInvoiceInput['document_type'],
  items: MarketingInvoiceItemInput[],
  freightValue: number,
  discountValue: number,
  payments: MarketingPaymentGroupInput[]
): void {
  if (!payments?.length) {
    throw new Error('Informe ao menos uma forma de pagamento.')
  }

  const expectedTotal = invoiceExpectedTotal(
    documentType,
    items,
    freightValue,
    discountValue
  )
  if (expectedTotal < 0) {
    throw new Error('O desconto da nota não pode ser maior que o total dos itens e frete.')
  }
  let paymentsTotal = 0

  for (const [groupIndex, group] of payments.entries()) {
    if (!group.payment_method_id?.trim()) {
      throw new Error(`Selecione a forma de pagamento no grupo ${groupIndex + 1}.`)
    }
    if (!group.installments?.length) {
      throw new Error(`Informe ao menos uma parcela no grupo ${groupIndex + 1}.`)
    }
    for (const [installmentIndex, installment] of group.installments.entries()) {
      const amount = Number(installment.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error(
          `Valor inválido na parcela ${installmentIndex + 1} do grupo ${groupIndex + 1}.`
        )
      }
      if (!installment.due_date?.trim()) {
        throw new Error(
          `Informe o vencimento da parcela ${installmentIndex + 1} do grupo ${groupIndex + 1}.`
        )
      }
      paymentsTotal += amount
    }
  }

  paymentsTotal = roundMoney(paymentsTotal)
  if (Math.abs(paymentsTotal - expectedTotal) > 0.009) {
    throw new Error(
      `A soma dos pagamentos (${paymentsTotal.toFixed(2)}) deve ser igual ao total da nota (${expectedTotal.toFixed(2)}).`
    )
  }
}

const INVOICE_SELECT = `
  *,
  supplier:suppliers(id, name),
  payment_source:payment_sources(id, name, cnpj),
  request:marketing_requests(id, request_number),
  supplier_quote:supplier_quotes(id, quote_number, title, supplier_id, request_id, status),
  campaign:marketing_campaigns(id, name),
  event:marketing_events(id, name),
  items:marketing_invoice_items(
    *,
    stock_item:marketing_stock_items(id, name, code, unit),
    allocations:marketing_invoice_allocations(
      *,
      location:marketing_stock_locations(id, name)
    )
  ),
  payment_templates(
    *,
    payment_method:payment_methods(id, name, settlement)
  ),
  payments(
    *,
    payment_method:payment_methods(id, name, settlement)
  )
`

function mapInvoice(row: MarketingInvoice): MarketingInvoice {
  const items = (row.items || [])
    .filter((item) => !item.deleted_at)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item) => ({
      ...item,
      quantity: Number(item.quantity),
      unit_value: Number(item.unit_value),
      discount_value: Number(item.discount_value || 0),
      total_value: Number(item.total_value),
      allocations: (item.allocations || []).map((a) => ({
        ...a,
        quantity: Number(a.quantity),
      })),
    }))
  const paymentTemplates = (row.payment_templates || [])
    .filter((template) => !template.deleted_at)
    .sort((a, b) => {
      if (a.group_index !== b.group_index) return a.group_index - b.group_index
      return a.installment_number - b.installment_number
    })
    .map(mapPaymentTemplate)
  const payments = ((row.payments || []) as MarketingPayment[])
    .filter((payment) => !payment.deleted_at)
    .sort((a, b) => {
      if (a.group_index !== b.group_index) return a.group_index - b.group_index
      return a.installment_number - b.installment_number
    })
    .map((payment): MarketingPayment => ({
      ...payment,
      amount: Number(payment.amount),
      group_index: Number(payment.group_index),
      installment_number: Number(payment.installment_number),
      recurrence_competence: payment.recurrence_competence || null,
      payment_status: payment.payment_status === 'paid' ? 'paid' : 'pending',
      paid_at: payment.paid_at || null,
      paid_by: payment.paid_by || null,
      paid_by_user: payment.paid_by_user || null,
    }))
  return {
    ...row,
    freight_value: Number(row.freight_value || 0),
    discount_value: Number(row.discount_value || 0),
    supplier_quote_id: row.supplier_quote_id || null,
    items,
    payment_templates: paymentTemplates,
    payments,
  }
}

function mapPaymentListRow(row: MarketingPaymentListRow): MarketingPaymentListRow {
  return {
    ...row,
    amount: Number(row.amount),
    group_index: Number(row.group_index),
    installment_number: Number(row.installment_number),
    recurrence_competence: row.recurrence_competence || null,
    payment_status: row.payment_status === 'paid' ? 'paid' : 'pending',
    paid_at: row.paid_at || null,
    paid_by: row.paid_by || null,
    paid_by_user: row.paid_by_user || null,
    invoice: row.invoice
      ? {
          ...row.invoice,
          supplier: row.invoice.supplier || null,
        }
      : null,
    contract: row.contract
      ? {
          ...row.contract,
          status: row.contract.status === 'confirmed' ? 'confirmed' : 'draft',
          supplier: row.contract.supplier || null,
        }
      : null,
  }
}

const PAYMENT_LIST_SELECT = `
  *,
  payment_method:payment_methods(id, name, settlement),
  paid_by_user:profiles!paid_by(id, name),
  invoice:invoices(
    id,
    number,
    series,
    document_type,
    status,
    issue_date,
    supplier_id,
    request_id,
    deleted_at,
    supplier:suppliers(id, name),
    request:marketing_requests(id, request_number)
  )
`

const CONTRACT_PAYMENT_REF_SELECT = `
  id,
  name,
  status,
  supplier_id,
  request_id,
  deleted_at,
  supplier:suppliers!contracts_supplier_id_fkey(id, name),
  request:marketing_requests(id, request_number)
`

function supabaseError(error: { message?: string } | null, fallback: string): Error {
  const message = error?.message?.trim()
  return new Error(message || fallback)
}

async function attachPaymentContracts(
  rows: MarketingPaymentListRow[]
): Promise<MarketingPaymentListRow[]> {
  const ids = [...new Set(rows.map((row) => row.contract_id).filter((id): id is string => Boolean(id)))]
  if (ids.length === 0) return rows

  const { data, error } = await supabase
    .from('contracts')
    .select(CONTRACT_PAYMENT_REF_SELECT)
    .in('id', ids)

  if (error) throw supabaseError(error, 'Erro ao carregar contratos das parcelas.')

  const byId = new Map(
    ((data || []) as unknown as NonNullable<MarketingPaymentListRow['contract']>[]).map((contract) => [
      contract.id,
      {
        ...contract,
        status: contract.status === 'confirmed' ? 'confirmed' : 'draft',
        supplier: Array.isArray(contract.supplier) ? contract.supplier[0] || null : contract.supplier || null,
      } as NonNullable<MarketingPaymentListRow['contract']>,
    ])
  )

  return rows.map((row) => {
    if (!row.contract_id) return row
    return { ...row, contract: byId.get(row.contract_id) || null }
  })
}

async function restoreInvoiceFinancialState(
  invoiceId: string,
  templates: ReturnType<typeof snapshotTemplateRows>,
  payments: ReturnType<typeof snapshotPaymentRows>
): Promise<void> {
  await paymentTemplatesService.restoreForOwner(
    { invoice_id: invoiceId },
    templates,
    { deleteReason: 'rollback restore templates' }
  )

  const rollbackPayload = softDeletePayload({ deleted_reason: 'rollback restore payments' })
  const { error: softPaymentsError } = await supabase
    .from('payments')
    .update(rollbackPayload)
    .eq('invoice_id', invoiceId)
    .is('deleted_at', null)
  if (softPaymentsError) throw softPaymentsError

  if (!payments.length) return
  const { error: insertPaymentsError } = await supabase.from('payments').insert(payments)
  if (insertPaymentsError) throw insertPaymentsError
}

function snapshotInvoiceItemRows(existing: MarketingInvoice): Array<Record<string, unknown>> {
  return (existing.items || [])
    .filter((item) => !item.deleted_at)
    .map((item) => ({
      invoice_id: existing.id,
      sort_order: item.sort_order,
      stock_item_id: item.stock_item_id,
      description: item.description,
      quantity: item.quantity,
      unit_value: item.unit_value,
      discount_value: item.discount_value,
      total_value: item.total_value,
    }))
}

async function restoreInvoiceUpdateState(
  existing: MarketingInvoice,
  itemRows: Array<Record<string, unknown>>,
  templates: ReturnType<typeof snapshotTemplateRows>,
  payments: ReturnType<typeof snapshotPaymentRows>
): Promise<void> {
  const { error: headerError } = await supabase
    .from('invoices')
    .update({
      document_type: existing.document_type,
      number: existing.number,
      series: existing.series,
      access_key: existing.access_key,
      freight_value: existing.freight_value,
      discount_value: existing.discount_value,
      supplier_id: existing.supplier_id,
      payment_source_id: existing.payment_source_id,
      issue_date: existing.issue_date,
      request_id: existing.request_id,
      supplier_quote_id: existing.supplier_quote_id,
      campaign_id: existing.campaign_id,
      event_id: existing.event_id,
      notes: existing.notes,
    })
    .eq('id', existing.id)
    .is('deleted_at', null)
  if (headerError) throw headerError

  const rollbackPayload = softDeletePayload({ deleted_reason: 'rollback restore items' })
  const { error: softItemsError } = await supabase
    .from('marketing_invoice_items')
    .update(rollbackPayload)
    .eq('invoice_id', existing.id)
    .is('deleted_at', null)
  if (softItemsError) throw softItemsError

  if (itemRows.length) {
    const { error: insertItemsError } = await supabase
      .from('marketing_invoice_items')
      .insert(itemRows)
    if (insertItemsError) throw insertItemsError
  }

  await restoreInvoiceFinancialState(existing.id, templates, payments)
}

export const marketingNotasService = {
  // ============================================
  // CAMPAIGNS
  // ============================================
  async listCampaigns(includeInactive = true): Promise<MarketingCampaign[]> {
    let query = supabase
      .from('marketing_campaigns')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as MarketingCampaign[]
  },

  async createCampaign(input: MarketingCampaignInput): Promise<MarketingCampaign> {
    const { data, error } = await supabase
      .from('marketing_campaigns')
      .insert({
        name: input.name.trim(),
        description: emptyToNull(input.description),
        starts_on: emptyToNull(input.starts_on),
        ends_on: emptyToNull(input.ends_on),
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingCampaign
  },

  async updateCampaign(
    id: string,
    input: Partial<MarketingCampaignInput>
  ): Promise<MarketingCampaign> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.description !== undefined) payload.description = emptyToNull(input.description)
    if (input.starts_on !== undefined) payload.starts_on = emptyToNull(input.starts_on)
    if (input.ends_on !== undefined) payload.ends_on = emptyToNull(input.ends_on)
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('marketing_campaigns')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingCampaign
  },

  async softDeleteCampaign(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('marketing_campaigns')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // EVENTS
  // ============================================
  async listEvents(includeInactive = true): Promise<MarketingEvent[]> {
    let query = supabase
      .from('marketing_events')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as MarketingEvent[]
  },

  async createEvent(input: MarketingEventInput): Promise<MarketingEvent> {
    const { data, error } = await supabase
      .from('marketing_events')
      .insert({
        name: input.name.trim(),
        description: emptyToNull(input.description),
        event_date: emptyToNull(input.event_date),
        location: emptyToNull(input.location),
        city: emptyToNull(input.city),
        state: emptyToNull(input.state)?.toUpperCase() || null,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingEvent
  },

  async updateEvent(id: string, input: Partial<MarketingEventInput>): Promise<MarketingEvent> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.description !== undefined) payload.description = emptyToNull(input.description)
    if (input.event_date !== undefined) payload.event_date = emptyToNull(input.event_date)
    if (input.location !== undefined) payload.location = emptyToNull(input.location)
    if (input.city !== undefined) payload.city = emptyToNull(input.city)
    if (input.state !== undefined) payload.state = emptyToNull(input.state)?.toUpperCase() || null
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('marketing_events')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingEvent
  },

  async softDeleteEvent(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('marketing_events')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // INVOICES
  // ============================================
  async listInvoices(filters?: MarketingInvoiceFilters): Promise<MarketingInvoice[]> {
    const requestNumber = parseRequestNumberFilter(filters?.requestNumber)
    let requestId: string | null = null
    if (requestNumber != null) {
      const { data: requestRow, error: requestError } = await supabase
        .from('marketing_requests')
        .select('id')
        .eq('request_number', requestNumber)
        .is('deleted_at', null)
        .maybeSingle()
      if (requestError) throw requestError
      if (!requestRow) return []
      requestId = requestRow.id
    }

    let query = supabase
      .from('invoices')
      .select(INVOICE_SELECT)
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (filters?.documentType) {
      query = query.eq('document_type', filters.documentType)
    }
    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.supplierId) {
      query = query.eq('supplier_id', filters.supplierId)
    }
    if (filters?.number?.trim()) {
      query = query.ilike('number', `%${filters.number.trim()}%`)
    }
    if (filters?.dateFrom) {
      query = query.gte('issue_date', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('issue_date', filters.dateTo)
    }
    if (requestId) {
      query = query.eq('request_id', requestId)
    }

    const { data, error } = await query
    if (error) throw error
    return ((data || []) as MarketingInvoice[]).map(mapInvoice)
  },

  async listByRequest(requestId: string): Promise<MarketingInvoice[]> {
    if (!requestId) return []
    const { data, error } = await supabase
      .from('invoices')
      .select(INVOICE_SELECT)
      .eq('request_id', requestId)
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return ((data || []) as MarketingInvoice[]).map(mapInvoice)
  },

  async getInvoice(id: string): Promise<MarketingInvoice | null> {
    const { data, error } = await supabase
      .from('invoices')
      .select(INVOICE_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    return mapInvoice(data as MarketingInvoice)
  },

  async createInvoice(
    input: MarketingInvoiceInput,
    createdBy?: string | null
  ): Promise<MarketingInvoice> {
    if (!input.supplier_id?.trim()) {
      throw new Error('Selecione o fornecedor.')
    }
    if (!input.payment_source_id?.trim()) {
      throw new Error('Selecione a fonte pagadora.')
    }
    if (!input.number?.trim()) {
      throw new Error('Informe o número da nota.')
    }
    if (!input.issue_date?.trim()) {
      throw new Error('Informe a data de emissão.')
    }
    validateItems(input.document_type, input.items)
    const freightValue = normalizeFreightValue(input.document_type, input.freight_value)
    const discountValue = normalizeDiscountValue(input.discount_value)
    validatePayments(
      input.document_type,
      input.items,
      freightValue,
      discountValue,
      input.payments
    )

    const { data: header, error: headerError } = await supabase
      .from('invoices')
      .insert({
        document_type: input.document_type,
        status: 'draft',
        number: input.number.trim(),
        series: emptyToNull(input.series),
        access_key: emptyToNull(input.access_key),
        freight_value: freightValue,
        discount_value: discountValue,
        supplier_id: input.supplier_id,
        payment_source_id: input.payment_source_id,
        issue_date: input.issue_date,
        request_id: emptyToNull(input.request_id),
        supplier_quote_id: emptyToNull(input.supplier_quote_id),
        campaign_id: emptyToNull(input.campaign_id),
        event_id: emptyToNull(input.event_id),
        notes: emptyToNull(input.notes),
        created_by: createdBy ?? null,
      })
      .select('id')
      .single()

    if (headerError) throw headerError

    const itemRows = input.items.map((item, index) => ({
      invoice_id: header.id,
      sort_order: item.sort_order ?? index * 10,
      stock_item_id: input.document_type === 'nfe' ? item.stock_item_id || null : null,
      description:
        input.document_type === 'nfse'
          ? item.description?.trim() || null
          : emptyToNull(item.description),
      quantity: item.quantity,
      unit_value: item.unit_value,
      discount_value: normalizeNonNegativeMoney(item.discount_value, 'desconto do item'),
      total_value: lineTotal(item),
    }))

    const { error: itemsError } = await supabase.from('marketing_invoice_items').insert(itemRows)
    if (itemsError) {
      await supabase
        .from('invoices')
        .update(softDeletePayload({ deleted_reason: 'rollback create items' }))
        .eq('id', header.id)
      throw itemsError
    }

    try {
      const templateResult = await paymentTemplatesService.replaceForOwner(
        { invoice_id: header.id },
        input.payments,
        {
          actorId: createdBy ?? null,
          deleteReason: 'initial payment template',
        }
      )
      const paymentRows = buildPaymentRowsFromTemplates(templateResult.rows)
      const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
      if (paymentsError) {
        throw paymentsError
      }
    } catch (paymentFlowError) {
      const rollbackPayload = softDeletePayload({
        deleted_by: createdBy ?? null,
        deleted_reason: 'rollback create templates/payments',
      })
      await supabase
        .from('payment_templates')
        .update(rollbackPayload)
        .eq('invoice_id', header.id)
        .is('deleted_at', null)
      await supabase
        .from('marketing_invoice_items')
        .update(rollbackPayload)
        .eq('invoice_id', header.id)
        .is('deleted_at', null)
      await supabase.from('payments').update(rollbackPayload).eq('invoice_id', header.id).is('deleted_at', null)
      await supabase.from('invoices').update(rollbackPayload).eq('id', header.id)
      throw paymentFlowError
    }

    const created = await this.getInvoice(header.id)
    if (!created) throw new Error('Nota criada, mas não foi possível recarregar.')
    if (input.supplier_quote_id) {
      await syncSupplierQuoteStatusSafe(
        input.supplier_quote_id,
        'nota_gerada',
        `createInvoice:${header.id}`
      )
    }
    return created
  },

  async updateInvoice(id: string, input: MarketingInvoiceInput): Promise<MarketingInvoice> {
    const existing = await this.getInvoice(id)
    if (!existing) throw new Error('Nota fiscal não encontrada.')
    if (existing.status !== 'draft') {
      throw new Error('Somente rascunhos podem ser editados.')
    }
    if (!input.supplier_id?.trim()) {
      throw new Error('Selecione o fornecedor.')
    }
    if (!input.payment_source_id?.trim()) {
      throw new Error('Selecione a fonte pagadora.')
    }
    if (!input.number?.trim()) {
      throw new Error('Informe o número da nota.')
    }
    if (!input.issue_date?.trim()) {
      throw new Error('Informe a data de emissão.')
    }
    validateItems(input.document_type, input.items)
    const freightValue = normalizeFreightValue(input.document_type, input.freight_value)
    const discountValue = normalizeDiscountValue(input.discount_value)
    validatePayments(
      input.document_type,
      input.items,
      freightValue,
      discountValue,
      input.payments
    )

    const previousQuoteId = existing.supplier_quote_id || null
    const nextQuoteId = emptyToNull(input.supplier_quote_id)

    const { error: headerError } = await supabase
      .from('invoices')
      .update({
        document_type: input.document_type,
        number: input.number.trim(),
        series: emptyToNull(input.series),
        access_key: emptyToNull(input.access_key),
        freight_value: freightValue,
        discount_value: discountValue,
        supplier_id: input.supplier_id,
        payment_source_id: input.payment_source_id,
        issue_date: input.issue_date,
        request_id: emptyToNull(input.request_id),
        supplier_quote_id: nextQuoteId,
        campaign_id: emptyToNull(input.campaign_id),
        event_id: emptyToNull(input.event_id),
        notes: emptyToNull(input.notes),
      })
      .eq('id', id)
      .eq('status', 'draft')
      .is('deleted_at', null)

    if (headerError) throw headerError

    const { error: softItemsError } = await supabase
      .from('marketing_invoice_items')
      .update(softDeletePayload({ deleted_reason: 'replaced on update' }))
      .eq('invoice_id', id)
      .is('deleted_at', null)

    if (softItemsError) throw softItemsError

    const itemRows = input.items.map((item, index) => ({
      invoice_id: id,
      sort_order: item.sort_order ?? index * 10,
      stock_item_id: input.document_type === 'nfe' ? item.stock_item_id || null : null,
      description:
        input.document_type === 'nfse'
          ? item.description?.trim() || null
          : emptyToNull(item.description),
      quantity: item.quantity,
      unit_value: item.unit_value,
      discount_value: normalizeNonNegativeMoney(item.discount_value, 'desconto do item'),
      total_value: lineTotal(item),
    }))

    const { error: itemsError } = await supabase.from('marketing_invoice_items').insert(itemRows)
    if (itemsError) throw itemsError

    const previousItemRows = snapshotInvoiceItemRows(existing)
    const previousPaymentTemplates = snapshotTemplateRows(existing.payment_templates)
    const previousPayments = snapshotPaymentRows(existing.payments)
    const { error: softPaymentsError } = await supabase
      .from('payments')
      .update(softDeletePayload({ deleted_reason: 'replaced on update' }))
      .eq('invoice_id', id)
      .is('deleted_at', null)
    if (softPaymentsError) throw softPaymentsError

    try {
      const templateResult = await paymentTemplatesService.replaceForOwner(
        { invoice_id: id },
        input.payments,
        {
          deleteReason: 'replaced payment template',
        }
      )
      const paymentRows = buildPaymentRowsFromTemplates(templateResult.rows)
      const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
      if (paymentsError) throw paymentsError
    } catch (paymentFlowError) {
      await restoreInvoiceUpdateState(
        existing,
        previousItemRows,
        previousPaymentTemplates,
        previousPayments
      )
      throw paymentFlowError
    }

    const updated = await this.getInvoice(id)
    if (!updated) throw new Error('Nota atualizada, mas não foi possível recarregar.')
    if (nextQuoteId) {
      await syncSupplierQuoteStatusSafe(nextQuoteId, 'nota_gerada', `updateInvoice:${id}:next`)
    }
    if (previousQuoteId && previousQuoteId !== nextQuoteId) {
      await syncSupplierQuoteStatusSafe(previousQuoteId, 'recebida', `updateInvoice:${id}:previous`)
    }
    return updated
  },

  async softDeleteInvoice(id: string, params?: SoftDeleteParams): Promise<void> {
    const existing = await this.getInvoice(id)
    if (!existing) throw new Error('Nota fiscal não encontrada.')
    if (existing.status !== 'draft') {
      throw new Error('Somente rascunhos podem ser excluídos.')
    }

    const payload = softDeletePayload(params)
    const { error: itemsError } = await supabase
      .from('marketing_invoice_items')
      .update(payload)
      .eq('invoice_id', id)
      .is('deleted_at', null)
    if (itemsError) throw itemsError

    const { error: paymentsError } = await supabase
      .from('payments')
      .update(payload)
      .eq('invoice_id', id)
      .is('deleted_at', null)
    if (paymentsError) throw paymentsError

    const { error: templatesError } = await supabase
      .from('payment_templates')
      .update(payload)
      .eq('invoice_id', id)
      .is('deleted_at', null)
    if (templatesError) throw templatesError

    const { error } = await supabase
      .from('invoices')
      .update(payload)
      .eq('id', id)
      .eq('status', 'draft')
      .is('deleted_at', null)

    if (error) throw error

    if (existing.supplier_quote_id) {
      await syncSupplierQuoteStatus(existing.supplier_quote_id, 'recebida')
    }
  },

  async confirmInvoice(
    id: string,
    allocations: MarketingInvoiceAllocationInput[] = []
  ): Promise<MarketingInvoice> {
    const existing = await this.getInvoice(id)
    if (!existing) throw new Error('Nota fiscal não encontrada.')
    if (existing.status !== 'draft') {
      throw new Error('Somente rascunhos podem ser confirmados.')
    }

    const payload =
      existing.document_type === 'nfe'
        ? allocations.map((a) => ({
            invoice_item_id: a.invoice_item_id,
            location_id: a.location_id,
            quantity: a.quantity,
          }))
        : []

    const { data, error } = await supabase.rpc('marketing_invoice_confirm', {
      p_invoice_id: id,
      p_allocations: payload,
    })

    if (error) throw error

    const confirmed = await this.getInvoice((data as MarketingInvoice).id || id)
    if (!confirmed) throw new Error('Nota confirmada, mas não foi possível recarregar.')
    return confirmed
  },

  // ============================================
  // PAYMENTS (financeiro)
  // ============================================
  async listPayments(filters?: MarketingPaymentFilters): Promise<MarketingPaymentListRow[]> {
    let query = supabase
      .from('payments')
      .select(PAYMENT_LIST_SELECT)
      .is('deleted_at', null)
      .order('due_date', { ascending: true })
      .order('installment_number', { ascending: true })

    if (filters?.dueFrom) query = query.gte('due_date', filters.dueFrom)
    if (filters?.dueTo) query = query.lte('due_date', filters.dueTo)
    if (filters?.paymentMethodId) {
      query = query.eq('payment_method_id', filters.paymentMethodId)
    }
    if (filters?.paymentStatus) {
      query = query.eq('payment_status', filters.paymentStatus)
    }

    const { data, error } = await query
    if (error) throw supabaseError(error, 'Erro ao carregar o financeiro.')

    let rows = await attachPaymentContracts(
      ((data || []) as MarketingPaymentListRow[]).map(mapPaymentListRow)
    )
    rows = rows.filter((row) => {
        if (row.invoice && !row.invoice.deleted_at) return true
        if (row.contract && !row.contract.deleted_at) return true
        return false
      })

    if (filters?.invoiceStatus) {
      rows = rows.filter((row) => {
        const status = row.invoice?.status || row.contract?.status
        return status === filters.invoiceStatus
      })
    }
    if (filters?.documentType === 'contract') {
      rows = rows.filter((row) => Boolean(row.contract))
    } else if (filters?.documentType) {
      rows = rows.filter((row) => row.invoice?.document_type === filters.documentType)
    }
    if (filters?.supplierId) {
      rows = rows.filter((row) => {
        const supplierId = row.invoice?.supplier_id || row.contract?.supplier_id
        return supplierId === filters.supplierId
      })
    }
    const requestNumber = parseRequestNumberFilter(filters?.requestNumber)
    if (requestNumber != null) {
      rows = rows.filter((row) => {
        const n =
          row.invoice?.request?.request_number ?? row.contract?.request?.request_number
        return n === requestNumber
      })
    }

    return rows
  },

  async getPayment(id: string): Promise<MarketingPaymentListRow | null> {
    const { data, error } = await supabase
      .from('payments')
      .select(PAYMENT_LIST_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw supabaseError(error, 'Erro ao carregar a parcela.')
    if (!data) return null
    const [row] = await attachPaymentContracts([mapPaymentListRow(data as MarketingPaymentListRow)])
    return row || null
  },

  async settlePayment(id: string, paidBy: string): Promise<MarketingPaymentListRow> {
    if (!paidBy) throw new Error('Usuário da baixa não identificado.')
    const existing = await this.getPayment(id)
    if (!existing) throw new Error('Parcela não encontrada.')
    const invoiceOk = Boolean(existing.invoice && !existing.invoice.deleted_at)
    const contractOk = Boolean(existing.contract && !existing.contract.deleted_at)
    if (!invoiceOk && !contractOk) {
      throw new Error('Nota ou contrato vinculado à parcela não encontrado.')
    }
    const parentStatus = existing.invoice?.status || existing.contract?.status
    if (parentStatus !== 'confirmed') {
      throw new Error('Só é possível baixar parcelas de notas ou contratos confirmados.')
    }
    if (existing.payment_status === 'paid') {
      throw new Error('Esta parcela já está baixada.')
    }

    const { error } = await supabase
      .from('payments')
      .update({
        payment_status: 'paid',
        paid_at: new Date().toISOString(),
        paid_by: paidBy,
      })
      .eq('id', id)
      .eq('payment_status', 'pending')
      .is('deleted_at', null)

    if (error) throw error

    const updated = await this.getPayment(id)
    if (!updated) throw new Error('Parcela baixada, mas não foi possível recarregar.')
    return updated
  },

  async reopenPayment(id: string): Promise<MarketingPaymentListRow> {
    const existing = await this.getPayment(id)
    if (!existing) throw new Error('Parcela não encontrada.')
    if (existing.payment_status !== 'paid') {
      throw new Error('Somente parcelas baixadas podem ser estornadas.')
    }

    const { error } = await supabase
      .from('payments')
      .update({
        payment_status: 'pending',
        paid_at: null,
        paid_by: null,
      })
      .eq('id', id)
      .eq('payment_status', 'paid')
      .is('deleted_at', null)

    if (error) throw error

    const updated = await this.getPayment(id)
    if (!updated) throw new Error('Parcela estornada, mas não foi possível recarregar.')
    return updated
  },
}
